import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { BookingDetailValues, bookingDetailEnum } from '../../services/reservations/options'
import { cn } from '../../utils/style'

/** Anchor of the reservation edit form on the detail page. */
export const RESERVATION_DETAILS_ANCHOR = 'reservation-details'

interface Props {
  missing: BookingDetailValues[]
  className?: string
}

/** Why TRA/SIRE are disabled: the reservation lacks a price and/or guest count. */
export const MissingBookingDetailsNotice: FC<Props> = ({ missing, className }) => {
  const { t } = useTranslation()
  if (!missing.length) return null
  const missingPrice = missing.includes(bookingDetailEnum.missingPrice)
  const missingGuestCount = missing.includes(bookingDetailEnum.missingGuestCount)
  const reason =
    missingPrice && missingGuestCount
      ? t('bookingDetails.missingBoth')
      : missingPrice
      ? t('bookingDetails.missingPrice')
      : t('bookingDetails.missingGuestCount')

  return (
    <div
      className={cn([
        'flex gap-1.5 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800',
        className,
      ])}
      data-testid="missing-booking-details"
    >
      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p>
        <span className="font-semibold">{reason}</span> {t('bookingDetails.calendarSyncHint')}{' '}
        <a
          href={`#${RESERVATION_DETAILS_ANCHOR}`}
          className="font-medium underline"
          data-testid="missing-booking-details-link"
        >
          {t('bookingDetails.editReservation')}
        </a>
      </p>
    </div>
  )
}

export const NeedsDetailsBadge: FC = () => {
  const { t } = useTranslation()
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800"
      title={t('bookingDetails.calendarSyncHint')}
      data-testid="needs-details-badge"
    >
      <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />
      {t('bookingDetails.needsDetails')}
    </span>
  )
}
